import axios from 'axios';
import { BASE_URL } from './helpers';
import { ModelRef, ReviewCapabilityFields, ReviewCheckSpec } from './structs/pipeline';

// ==================== Reviewer settings ====================

/** How a run reacts to AI review verdicts. */
export type ReviewMode = 'off' | 'on' | 'strict';

/** Which confirmed failures are allowed to trigger a regeneration. */
export type ReviewSeverityGate = 'blocking' | 'any';

/**
 * Bounds on what the reviewer may DO to a run/pipeline, as opposed to what it
 * judges. Absent (pre-A1) settings mean today's engine behaviour, which is
 * why every field here defaults to the current always-on behaviour except
 * `attachment_rewire`, which defaults off since it lets the reviewer swap
 * what a checkpoint feeds into generation.
 */
export interface ReviewerPermissions {
  /** Allow the confirm-tier regen+pick cascade to run at all. */
  pipeline_regenerate: boolean;
  /** Alias of `max_attempts` — the two are kept in sync by this panel. */
  pipeline_max_attempts: number;
  /** Honour confirm-tier `metadata.reconcile` scene-plan patches. */
  reconcile: boolean;
  /** Honour a confirm-tier verdict's `metadata.rewire` attachment proposal
   *  on the regeneration attempt (drop/add prior-checkpoint attachments). */
  attachment_rewire: boolean;
  /**
   * Honour a confirm-tier verdict's `metadata.drop_closing_frame`: the
   * reviewer may switch a scene's closing frame OFF (instead of regenerating
   * it) when it judges the clip converges better as a one-frame scene. The
   * frame stays browsable and one click re-enables it. Opt-in.
   */
  drop_closing_frame: boolean;
  /**
   * Honour a pair verdict's `metadata.pair.video_prompt_adjustment`: the
   * reviewer may rewrite a scene's video prompt (the motion brief the clip is
   * animated from) when it judges both frames are right and the movement was
   * mis-described. A second judge — the keeps check — must first confirm the
   * rewrite keeps the characters, beats, dialogue and duration. ON by default
   * since 2026-09-26: a small mismatch is better fixed by rewriting the motion
   * than by re-rolling the image.
   */
  adjust_video_prompt: boolean;
  /**
   * Let the reviewer read a scene's prompts BEFORE the first image is generated
   * and rewrite them inside the approved story. The cheapest place a prompt can
   * be fixed — nothing has been paid for yet — and it never blocks: a pass that
   * fails simply proceeds with the original prompts. ON by default, which is why
   * a section that omits the key still parses to true.
   */
  adjust_prompts_before_generation: boolean;
  /**
   * Honour a verdict's `metadata.cross_scene`: the reviewer may regenerate an
   * EARLIER scene that contradicts the frame it is judging, instead of only
   * reporting the contradiction. Opt-in — it spends generations on a scene the
   * operator already accepted. The suggestion is shown either way; without the
   * permission it comes with a button rather than an action.
   */
  cross_scene_regenerate: boolean;
  /** How many earlier-scene fixes one run may spend, 0-10. */
  cross_scene_max_per_run: number;
}

/** Clip QA (Phase 2) only ever regenerates or blocks; never "off" mid-mode. */
export type ClipQAMode = 'on' | 'strict';

/**
 * What the clip judge is shown: `auto` sends the rendered clip itself
 * (with audio) to judge models that accept video input, falling back to
 * sampled frames wherever a model/provider/size can't take it; `frames`
 * always uses today's timestamped-frames path.
 */
export type ClipQAVideoInput = 'auto' | 'frames';

/** A model reference distinct from the confirm tier's, for the clip judge. */
export interface ClipQAModelRef {
  provider: string;
  model: string;
}

/**
 * Per-clip (ClipPrompt / Phase-2) review settings — separate from the
 * pipeline-level reviewer above. Re-renders always land in new, detached
 * media items; QA never overwrites the canonical clip in place.
 */
export interface ClipQASettings {
  enabled: boolean;
  mode: ClipQAMode;
  /** Re-render attempts per clip, 0-2. */
  max_rerenders: number;
  /** Write trim_start/trim_end from the verdict. */
  allow_trim: boolean;
  /** Whether MaxRerenders may ever be spent. */
  allow_rerender: boolean;
  /** Absent ⇒ inherit the pipeline reviewer's confirm model. */
  model?: ClipQAModelRef;
  /** Minimum confidence a FAIL needs before it counts, 0-1; 0 ⇒ inherit. */
  confidence_gate: number;
  /** What the clip judge is shown: the clip itself, or sampled frames. */
  video_input: ClipQAVideoInput;
}

/**
 * User-scoped reviewer defaults, stored under the `reviewer` key of the user
 * settings object. A checkpoint's own `review` block overrides any of these.
 */
export interface ReviewerSettings {
  /** Cheap tier — judges every reviewed item. */
  check_provider: string;
  check_model: string;
  /** Strong tier — adjudicates what the cheap tier failed. */
  confirm_provider: string;
  confirm_model: string;
  default_review_mode: ReviewMode;
  /** Minimum confidence a FAIL needs before it counts, 0-1. */
  confidence_gate: number;
  /** Regeneration attempts per reviewed checkpoint, 0-3. */
  max_attempts: number;
  severity_gate: ReviewSeverityGate;
  /** What the reviewer is allowed to DO (regen/reconcile/rewire/block). */
  permissions: ReviewerPermissions;
  /** Per-clip (Phase-2) QA settings. */
  clip_qa: ClipQASettings;
  /**
   * The account's baseline for the ten review capability fields — what the
   * judge is SHOWN and what it is told to rule on (`types.ReviewCapabilities`).
   *
   * Optional at every level, and deliberately so: a missing field means
   * "never chose", which the engine reads as inherit. Materialising a zero
   * block here would turn that into a deliberate all-off, which is a different
   * statement — so the parser never invents one.
   */
  capabilities?: ReviewCapabilityFields;
}

/**
 * What the engine assumes when the user has never saved a reviewer section.
 * Mirrors the Go hard defaults exactly — the panel must show the same values
 * the run would actually use.
 */
export const DEFAULT_REVIEWER_SETTINGS: ReviewerSettings = {
  check_provider: 'openrouter',
  check_model: 'google/gemini-2.5-flash-lite',
  confirm_provider: 'openrouter',
  confirm_model: 'google/gemini-2.5-flash',
  default_review_mode: 'off',
  confidence_gate: 0.85,
  max_attempts: 2,
  severity_gate: 'blocking',
  permissions: {
    pipeline_regenerate: true,
    pipeline_max_attempts: 2,
    reconcile: true,
    attachment_rewire: false,
    drop_closing_frame: false,
    adjust_video_prompt: true,
    adjust_prompts_before_generation: true,
    cross_scene_regenerate: false,
    cross_scene_max_per_run: 2,
  },
  clip_qa: {
    enabled: false,
    mode: 'on',
    max_rerenders: 1,
    allow_trim: true,
    allow_rerender: true,
    confidence_gate: 0,
    video_input: 'auto',
  },
};

export const REVIEW_MODES: ReviewMode[] = ['off', 'on', 'strict'];
export const REVIEW_SEVERITY_GATES: ReviewSeverityGate[] = ['blocking', 'any'];
export const CLIP_QA_MODES: ClipQAMode[] = ['on', 'strict'];
export const CLIP_QA_VIDEO_INPUT_MODES: ClipQAVideoInput[] = ['auto', 'frames'];

const asString = (value: unknown, fallback: string): string =>
  typeof value === 'string' && value.trim() !== '' ? value.trim() : fallback;

const asNumber = (value: unknown, fallback: number): number => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const asBoolean = (value: unknown, fallback: boolean): boolean =>
  typeof value === 'boolean' ? value : fallback;

/**
 * The backend's `ReviewerPermissionsRevision`: stamped on every permissions
 * section saved since the motion permission's default flipped to ON.
 */
const PERMISSIONS_REVISION = 2;

/**
 * `adjust_video_prompt`, read exactly as the engine reads it
 * (`AdjustVideoPromptEnabled`). The field used to be a plain bool, so every
 * section saved before the flip says `false` whether or not anybody chose it;
 * only a `false` carrying the revision is a choice. Read the stored value
 * instead and a legacy account's panel shows OFF for a run that uses ON — and
 * its next Save, which always sends the field, pins OFF for good.
 */
const parseAdjustVideoPrompt = (section: Record<string, unknown>, fallback: boolean): boolean => {
  const chosenSinceFlip = asNumber(section.revision, 0) >= PERMISSIONS_REVISION;
  if (section.adjust_video_prompt === false && !chosenSinceFlip) return fallback;
  return asBoolean(section.adjust_video_prompt, fallback);
};

/** The `permissions` object, every field defaulted independently. */
const parsePermissions = (value: unknown): ReviewerPermissions => {
  const defaults = DEFAULT_REVIEWER_SETTINGS.permissions;
  const section = value && typeof value === 'object' ? (value as Record<string, unknown>) : undefined;
  if (!section) return { ...defaults };
  return {
    pipeline_regenerate: asBoolean(section.pipeline_regenerate, defaults.pipeline_regenerate),
    pipeline_max_attempts: asNumber(section.pipeline_max_attempts, defaults.pipeline_max_attempts),
    reconcile: asBoolean(section.reconcile, defaults.reconcile),
    attachment_rewire: asBoolean(section.attachment_rewire, defaults.attachment_rewire),
    drop_closing_frame: asBoolean(section.drop_closing_frame, defaults.drop_closing_frame),
    adjust_video_prompt: parseAdjustVideoPrompt(section, defaults.adjust_video_prompt),
    // Absent is "nobody said", and the built-in answer is ON — the wire omits
    // the key for every grant list stored before this permission existed.
    adjust_prompts_before_generation: asBoolean(
      section.adjust_prompts_before_generation,
      defaults.adjust_prompts_before_generation
    ),
    // A null here is the wire's "inherit", which at the ACCOUNT layer — the top
    // of the cascade — means the built-in default. `?? undefined` is what makes
    // the number read it that way: `Number(null)` is a perfectly finite 0.
    cross_scene_regenerate: asBoolean(section.cross_scene_regenerate, defaults.cross_scene_regenerate),
    cross_scene_max_per_run: asNumber(
      section.cross_scene_max_per_run ?? undefined,
      defaults.cross_scene_max_per_run
    ),
  };
};

/** A `model` ref is only valid with both fields present; otherwise inherit. */
const parseClipQAModel = (value: unknown): ClipQAModelRef | undefined => {
  if (!value || typeof value !== 'object') return undefined;
  const record = value as Record<string, unknown>;
  const provider = asString(record.provider, '');
  const model = asString(record.model, '');
  return provider && model ? { provider, model } : undefined;
};

/** The `clip_qa` object, every field defaulted independently. */
const parseClipQASettings = (value: unknown): ClipQASettings => {
  const defaults = DEFAULT_REVIEWER_SETTINGS.clip_qa;
  const section = value && typeof value === 'object' ? (value as Record<string, unknown>) : undefined;
  if (!section) return { ...defaults };

  const mode = asString(section.mode, defaults.mode);
  const model = parseClipQAModel(section.model);
  const videoInput = asString(section.video_input, defaults.video_input);

  return {
    enabled: asBoolean(section.enabled, defaults.enabled),
    mode: (CLIP_QA_MODES as string[]).includes(mode) ? (mode as ClipQAMode) : defaults.mode,
    max_rerenders: asNumber(section.max_rerenders, defaults.max_rerenders),
    allow_trim: asBoolean(section.allow_trim, defaults.allow_trim),
    allow_rerender: asBoolean(section.allow_rerender, defaults.allow_rerender),
    ...(model ? { model } : {}),
    confidence_gate: asNumber(section.confidence_gate, defaults.confidence_gate),
    video_input: (CLIP_QA_VIDEO_INPUT_MODES as string[]).includes(videoInput)
      ? (videoInput as ClipQAVideoInput)
      : defaults.video_input,
  };
};

/** A pair/check/confirm ref, kept only for the halves that are really there. */
const parseCapabilityModelRef = (value: unknown): ModelRef | undefined => {
  if (!value || typeof value !== 'object') return undefined;
  const record = value as Record<string, unknown>;
  const ref: ModelRef = {};
  const provider = asString(record.provider, '');
  const model = asString(record.model, '');
  if (provider) ref.provider = provider;
  if (model) ref.model = model;
  return ref.provider || ref.model ? ref : undefined;
};

/** The fixed checks a judge must verify verbatim; entries without text are noise. */
const parseFixedChecks = (value: unknown): ReviewCheckSpec[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const checks: ReviewCheckSpec[] = [];
  for (const entry of value) {
    if (!entry || typeof entry !== 'object') continue;
    const record = entry as Record<string, unknown>;
    const check = asString(record.check, '');
    if (!check) continue;
    checks.push(
      typeof record.required === 'boolean' && record.required ? { check, required: true } : { check }
    );
  }
  return checks.length > 0 ? checks : undefined;
};

/**
 * The `capabilities` block, if the user ever saved one.
 *
 * Every field is kept ONLY when the stored value is the shape the engine
 * validates, and the whole block collapses to `undefined` when nothing
 * survives — absent means inherit, and a half-invented block would make the
 * account claim a choice it never made. Range and vocabulary are the engine's
 * to enforce (it rejects the PUT); this only refuses values of the wrong TYPE.
 */
const parseReviewCapabilities = (value: unknown): ReviewCapabilityFields | undefined => {
  if (!value || typeof value !== 'object') return undefined;
  const record = value as Record<string, unknown>;
  const capabilities: ReviewCapabilityFields = {};

  if (typeof record.story === 'boolean') capabilities.story = record.story;
  if (typeof record.scene_scope === 'number' && Number.isFinite(record.scene_scope)) {
    capabilities.scene_scope = record.scene_scope;
  }
  if (typeof record.descriptions === 'boolean') capabilities.descriptions = record.descriptions;
  if (typeof record.attempt_history === 'boolean') {
    capabilities.attempt_history = record.attempt_history;
  }
  if (typeof record.cast === 'boolean') capabilities.cast = record.cast;
  if (typeof record.pair_judge === 'boolean') capabilities.pair_judge = record.pair_judge;

  const pair = parseCapabilityModelRef(record.pair);
  if (pair) capabilities.pair = pair;

  const fixedChecks = parseFixedChecks(record.fixed_checks);
  if (fixedChecks) capabilities.fixed_checks = fixedChecks;

  if (Array.isArray(record.lenses)) {
    const lenses = record.lenses
      .filter((lens): lens is string => typeof lens === 'string')
      .map((lens) => lens.trim())
      .filter((lens) => lens !== '');
    if (lenses.length > 0) capabilities.lenses = lenses;
  }

  const guidance = asString(record.guidance, '');
  if (guidance) capabilities.guidance = guidance;

  return Object.keys(capabilities).length > 0 ? capabilities : undefined;
};

/**
 * The `reviewer` section of a settings object, with every missing or malformed
 * field filled from the hard defaults. Settings are an open JSONB blob, so a
 * partial section (or none at all) is the normal case, never an error.
 */
export const parseReviewerSettings = (settings: unknown): ReviewerSettings => {
  const section =
    settings && typeof settings === 'object'
      ? ((settings as Record<string, unknown>).reviewer as Record<string, unknown> | undefined)
      : undefined;
  if (!section || typeof section !== 'object') return { ...DEFAULT_REVIEWER_SETTINGS };

  const mode = asString(section.default_review_mode, DEFAULT_REVIEWER_SETTINGS.default_review_mode);
  const severity = asString(section.severity_gate, DEFAULT_REVIEWER_SETTINGS.severity_gate);
  const capabilities = parseReviewCapabilities(section.capabilities);

  return {
    check_provider: asString(section.check_provider, DEFAULT_REVIEWER_SETTINGS.check_provider),
    check_model: asString(section.check_model, DEFAULT_REVIEWER_SETTINGS.check_model),
    confirm_provider: asString(section.confirm_provider, DEFAULT_REVIEWER_SETTINGS.confirm_provider),
    confirm_model: asString(section.confirm_model, DEFAULT_REVIEWER_SETTINGS.confirm_model),
    default_review_mode: (REVIEW_MODES as string[]).includes(mode)
      ? (mode as ReviewMode)
      : DEFAULT_REVIEWER_SETTINGS.default_review_mode,
    confidence_gate: asNumber(section.confidence_gate, DEFAULT_REVIEWER_SETTINGS.confidence_gate),
    max_attempts: asNumber(section.max_attempts, DEFAULT_REVIEWER_SETTINGS.max_attempts),
    severity_gate: (REVIEW_SEVERITY_GATES as string[]).includes(severity)
      ? (severity as ReviewSeverityGate)
      : DEFAULT_REVIEWER_SETTINGS.severity_gate,
    permissions: parsePermissions(section.permissions),
    clip_qa: parseClipQASettings(section.clip_qa),
    ...(capabilities ? { capabilities } : {}),
  };
};

// ==================== Media settings ====================

/**
 * User-scoped media handling, stored under the `media` key of the user
 * settings object. One field so far: whether an uploaded image is described
 * for the generators and the reviewer when it lands.
 */
export interface MediaSettings {
  /**
   * Describe every uploaded image once, with one small vision call, so what it
   * SHOWS can be told to generators and to the judge. On by default: an
   * undescribed upload is a reference nothing downstream can read.
   */
  auto_describe_uploads: boolean;
}

/** What the engine assumes when the user has never saved a media section. */
export const DEFAULT_MEDIA_SETTINGS: MediaSettings = { auto_describe_uploads: true };

/** The `media` section, with a missing or malformed field left at its default. */
export const parseMediaSettings = (value: unknown): MediaSettings => {
  const section = value && typeof value === 'object' ? (value as Record<string, unknown>) : undefined;
  return {
    auto_describe_uploads: asBoolean(
      section?.auto_describe_uploads,
      DEFAULT_MEDIA_SETTINGS.auto_describe_uploads
    ),
  };
};

// ==================== Generation settings ====================

/** One locked generator: the provider, the model id and that model's own settings. */
export interface GenerationModel {
  provider: string;
  model: string;
  /** Size and the like, as the model's Settings dialog writes them. */
  settings?: Record<string, unknown>;
}

/** How a video model is driven: from a shot's frames, or from reference pictures. */
export type VideoMode = 'frames' | 'references';

/**
 * One video model the user allows. `modes` says what it takes; the server
 * works that out from the registry and ignores whatever a save sends. An empty
 * list means "not known yet" (a model just picked, or an answer that came
 * without one): the server never saves a model that takes no mode at all.
 */
export interface VideoModelEntry extends GenerationModel {
  modes: VideoMode[];
}

/** The set of video models the user allows; the director picks among them per clip. */
export interface VideoGenerationSettings {
  models: VideoModelEntry[];
  /** The `model` id of the entry a clip runs on when the director names none. */
  default: string;
  /** Off: no shot may use reference pictures, whichever model takes them. */
  reference_mode: boolean;
}

/** How many video models one record may hold (the server refuses more). */
export const MAX_VIDEO_MODELS = 4;

/** A picture's size in pixels. */
export interface ImageSize {
  width: number;
  height: number;
}

/** The model the run's agents work on; the pair always moves together. */
export interface AgentModelChoice {
  provider: string;
  model: string;
}

/**
 * User-scoped locked generation settings, stored under the `generation` key of
 * the user settings object. A pipeline marked `generation_settings: "user"`
 * (the crew) always runs on these; every other pipeline is unchanged.
 */
export interface GenerationSettings {
  image: GenerationModel;
  /** Pictures of pipelines that end in a carousel, on the same image model. */
  carousel_image: ImageSize;
  video: VideoGenerationSettings;
  /** The agents' model; `null` = the system default. */
  agent_model: AgentModelChoice | null;
  /** The system default itself. Only the server's answer carries it; a save never sends it. */
  agent_model_default?: AgentModelChoice;
  /** Reserved: stored and shown, read by nothing yet. */
  audio?: GenerationModel | null;
  /** What one agent step may spend on its own model calls, USD. */
  agent_model_cap_usd: number;
  /** The most image.generate calls one agent may make. */
  max_image_generations: number;
}

/** What a save carries: the record without the parts only the server writes. */
export interface GenerationPayload extends Omit<GenerationSettings, 'video' | 'agent_model_default'> {
  video: { models: GenerationModel[]; default: string; reference_mode: boolean };
}

/** The server's bounds, so the inputs can state them; the server enforces them. */
export const GENERATION_MODEL_CAP_USD = { min: 0.05, max: 5, step: 0.05 } as const;
export const GENERATION_MAX_IMAGE_GENERATIONS = { min: 1, max: 40 } as const;
/** The bounds on each side of the carousel picture, in pixels. */
export const CAROUSEL_IMAGE_SIDE = { min: 64, max: 8192 } as const;

/**
 * What the engine assumes when the user has never saved a generation section.
 * Mirrors the Go hard defaults (`HardDefaultGenerationSettings`) exactly — the
 * panel must show the models the run would actually use. These are defaults the
 * user can change, not models any step is bound to.
 */
export const DEFAULT_GENERATION_SETTINGS: GenerationSettings = {
  image: {
    provider: 'runware',
    model: 'openai:gpt-image@2.5-flare',
    settings: { width: 720, height: 1280 },
  },
  carousel_image: { width: 1088, height: 1408 },
  video: {
    models: [
      {
        provider: 'vertexai',
        model: 'veo-3.1-lite-generate-001',
        settings: { width: 720, height: 1280 },
        modes: ['frames'],
      },
      {
        provider: 'vertexai',
        model: 'veo-3.1-fast-generate-001',
        settings: { width: 720, height: 1280 },
        modes: ['frames', 'references'],
      },
    ],
    default: 'veo-3.1-lite-generate-001',
    reference_mode: true,
  },
  agent_model: null,
  agent_model_cap_usd: 0.75,
  max_image_generations: 30,
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const cloneGenerationModel = (model: GenerationModel): GenerationModel => ({
  provider: model.provider,
  model: model.model,
  ...(model.settings ? { settings: { ...model.settings } } : {}),
});

/** A model ref is a unit: both halves present, or it is not a ref at all. */
const readGenerationModel = (value: unknown): GenerationModel | undefined => {
  if (!isRecord(value)) return undefined;
  const provider = asString(value.provider, '');
  const model = asString(value.model, '');
  if (!provider || !model) return undefined;
  return { provider, model, ...(isRecord(value.settings) ? { settings: { ...value.settings } } : {}) };
};

/** Unlike `asNumber`, null and '' are not 0: only a real number counts. */
const asFiniteNumber = (value: unknown, fallback: number): number => {
  const parsed = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  return typeof parsed === 'number' && Number.isFinite(parsed) ? parsed : fallback;
};

const VIDEO_MODES: readonly VideoMode[] = ['frames', 'references'];

const cloneVideoEntry = (entry: VideoModelEntry): VideoModelEntry => ({
  ...cloneGenerationModel(entry),
  modes: [...entry.modes],
});

/** Two whole numbers within the bounds, or nothing: a size that cannot be right is not read. */
const readImageSize = (value: unknown): ImageSize | undefined => {
  if (!isRecord(value)) return undefined;
  const { width, height } = value;
  const inBounds = (side: unknown): side is number =>
    typeof side === 'number' &&
    Number.isInteger(side) &&
    side >= CAROUSEL_IMAGE_SIDE.min &&
    side <= CAROUSEL_IMAGE_SIDE.max;
  return inBounds(width) && inBounds(height) ? { width, height } : undefined;
};

const cloneVideo = (video: VideoGenerationSettings): VideoGenerationSettings => ({
  ...video,
  models: video.models.map(cloneVideoEntry),
});

const readVideoEntry = (value: unknown): VideoModelEntry | undefined => {
  const model = readGenerationModel(value);
  if (!model) return undefined;
  const modes = isRecord(value) && Array.isArray(value.modes) ? value.modes : [];
  return { ...model, modes: VIDEO_MODES.filter((mode) => modes.includes(mode)) };
};

/**
 * The set a record that named one model per mode stands for: the frames model,
 * which is the default, and the references model when there is one. A
 * references model that is the frames model joins its entry. No references
 * model is reference mode off.
 */
const videoFromLegacy = (
  frames: GenerationModel,
  references: GenerationModel | null
): VideoGenerationSettings => {
  const first: VideoModelEntry = { ...cloneGenerationModel(frames), modes: ['frames'] };
  const video: VideoGenerationSettings = {
    models: [first],
    default: frames.model,
    reference_mode: references != null,
  };
  if (!references) return video;
  if (references.provider === frames.provider && references.model === frames.model) {
    first.modes.push('references');
  } else {
    video.models.push({ ...cloneGenerationModel(references), modes: ['references'] });
  }
  return video;
};

/**
 * The video part of a whole record (a server answer, or the snapshot a run
 * keeps), in the set shape or the older {frames, references} one. Nothing found
 * is `undefined`: unlike a stored partial section, a whole record has no
 * defaults to lay under it, and an older record without `references` has reference mode off.
 */
export const parseEffectiveVideo = (value: unknown): VideoGenerationSettings | undefined => {
  if (!isRecord(value)) return undefined;
  if (Array.isArray(value.models)) {
    const models = value.models.map(readVideoEntry).filter((m): m is VideoModelEntry => Boolean(m));
    if (models.length === 0) return undefined;
    const wanted = asString(value.default, '');
    return {
      models,
      default: models.some((m) => m.model === wanted) ? wanted : models[0].model,
      reference_mode:
        typeof value.reference_mode === 'boolean'
          ? value.reference_mode
          : DEFAULT_GENERATION_SETTINGS.video.reference_mode,
    };
  }
  const frames = readGenerationModel(value.frames);
  return frames ? videoFromLegacy(frames, readGenerationModel(value.references) ?? null) : undefined;
};

/** A stored partial section's video: a part that is missing keeps its default. */
const parseVideo = (value: unknown): VideoGenerationSettings => {
  const defaults = DEFAULT_GENERATION_SETTINGS.video;
  const video = isRecord(value) ? value : {};
  if (!Array.isArray(video.models) && ('frames' in video || 'references' in video)) {
    const [defaultFrames, defaultReferences] = defaults.models;
    return videoFromLegacy(
      readGenerationModel(video.frames) ?? defaultFrames,
      video.references === null ? null : readGenerationModel(video.references) ?? defaultReferences
    );
  }
  return parseEffectiveVideo(video) ?? cloneVideo(defaults);
};

/** An agent model comes whole or not at all; anything else is the system default (`null`). */
const readAgentModel = (value: unknown): AgentModelChoice | null => {
  if (!isRecord(value)) return null;
  const provider = asString(value.provider, '');
  const model = asString(value.model, '');
  return provider && model ? { provider, model } : null;
};

/**
 * The `generation` section, laid over the hard defaults the way the server lays
 * a stored section over them: a missing key keeps its default. A model ref
 * comes whole or not at all (a provider without its model is a mismatched pair).
 * The video part is read in the set shape or the older {frames, references}
 * one, where an explicit `references: null` is a choice: reference mode off.
 */
export const parseGenerationSettings = (value: unknown): GenerationSettings => {
  const defaults = DEFAULT_GENERATION_SETTINGS;
  const section = isRecord(value) ? value : {};
  const audio = readGenerationModel(section.audio);
  const agentModelDefault = readAgentModel(section.agent_model_default);

  return {
    image: readGenerationModel(section.image) ?? cloneGenerationModel(defaults.image),
    carousel_image: readImageSize(section.carousel_image) ?? { ...defaults.carousel_image },
    video: parseVideo(section.video),
    agent_model: readAgentModel(section.agent_model),
    ...(agentModelDefault ? { agent_model_default: agentModelDefault } : {}),
    ...(audio ? { audio } : {}),
    agent_model_cap_usd: asFiniteNumber(section.agent_model_cap_usd, defaults.agent_model_cap_usd),
    max_image_generations: asFiniteNumber(section.max_image_generations, defaults.max_image_generations),
  };
};

/**
 * The server's answer to GET/PUT is the EFFECTIVE record — already merged over
 * its defaults. An answer in the older shape leaves out a `references` that is
 * off; reading that absence as "default" (right for a stored partial section)
 * would show a model the user switched off, and the next Save would switch it
 * back on.
 */
const parseEffectiveGeneration = (value: unknown): GenerationSettings => {
  if (!isRecord(value) || !isRecord(value.video)) return parseGenerationSettings(value);
  return parseGenerationSettings({
    ...value,
    video: { ...value.video, references: value.video.references ?? null },
  });
};

/** Does a PUT response carry the record, rather than an empty or `{ok:true}` body? */
const looksLikeGenerationRecord = (value: unknown): boolean =>
  isRecord(value) && isRecord(value.image) && isRecord(value.video);

/**
 * Video settings the locked record may not carry: a clip's duration is chosen
 * per shot, and the server refuses a record that sets one. The dialog hides
 * them and the payload strips them, so no path can send one.
 */
export const PER_SHOT_VIDEO_SETTINGS = ['duration'] as const;

const withoutEmptySettings = (model: GenerationModel): GenerationModel => {
  const { settings, ...rest } = model;
  return settings && Object.keys(settings).length > 0 ? { ...rest, settings } : rest;
};

const withoutPerShotSettings = (model: GenerationModel): GenerationModel => {
  if (!model.settings) return model;
  const settings = { ...model.settings };
  PER_SHOT_VIDEO_SETTINGS.forEach((key) => delete settings[key]);
  return { ...model, settings };
};

const videoPayload = (model: GenerationModel): GenerationModel =>
  withoutEmptySettings(withoutPerShotSettings(model));

/**
 * The body to PUT. A choice is always explicit (reference mode off is `false`,
 * the default agent model is `null`), empty model settings are left out, the
 * video models carry no per-shot duration and no modes (the server computes
 * those), the system default agent model is not sent (it is the server's), and
 * the image-generation count is a whole number. The carousel size goes as its two numbers.
 */
export const buildGenerationPayload = (values: GenerationSettings): GenerationPayload => ({
  image: withoutEmptySettings(values.image),
  carousel_image: { width: values.carousel_image.width, height: values.carousel_image.height },
  video: {
    models: values.video.models.map(({ provider, model, settings }) =>
      videoPayload({ provider, model, settings })
    ),
    default: values.video.default,
    reference_mode: values.video.reference_mode,
  },
  agent_model: values.agent_model
    ? { provider: values.agent_model.provider, model: values.agent_model.model }
    : null,
  ...(values.audio ? { audio: values.audio } : {}),
  agent_model_cap_usd: values.agent_model_cap_usd,
  max_image_generations: Math.round(values.max_image_generations),
});

// ==================== Agent tool settings ====================

/**
 * What the chat agent's own tool calls may do: the `agent_tools` section, with
 * the two values the server serves beside it. The locked image model (the one
 * the agent may always use) and the carousel size are set on the generation
 * screen and only shown here; a save never sends them.
 */
export interface AgentToolSettings {
  /** Image models the agent may use besides the locked one. */
  image_models: AgentModelChoice[];
  /** Models `text_structured` may use besides the thread's own; empty means the thread's only. */
  writing_models: AgentModelChoice[];
  /** What the assistant's own model calls may cost in one production, USD. */
  agent_cap_usd: number;
  /** The most all of a production's approved budget cards may add up to, USD. */
  max_budget_usd: number;
  paid_x_search: boolean;
  paid_photo_search: boolean;
  /** Label content that uses AI pictures: on screen where the style has one, and in the credits. */
  ai_labels: boolean;
  locked_image_model: AgentModelChoice;
  carousel_size: ImageSize;
}

/** What a save carries: the section alone. */
export type AgentToolSettingsPayload = Omit<AgentToolSettings, 'locked_image_model' | 'carousel_size'>;

/** The server's bounds, so the inputs can state them; the server enforces them. */
export const AGENT_TOOL_CAP_USD = { min: 0.5, max: 50, step: 0.5 } as const;
export const AGENT_TOOL_BUDGET_USD = { min: 0.5, max: 200, step: 0.5 } as const;

const readModelRefs = (value: unknown): AgentModelChoice[] =>
  (Array.isArray(value) ? value : []).flatMap((entry): AgentModelChoice[] => {
    const ref = readAgentModel(entry);
    return ref ? [ref] : [];
  });

/**
 * The server's answer to GET/PUT: the effective settings, already laid over
 * its defaults. A key that is missing reads as the server's default ($5 cap,
 * $10 budget, both searches and the AI labels on), so the panel never shows a
 * value the agent would not get.
 */
export const parseAgentToolSettings = (value: unknown): AgentToolSettings => {
  const record = isRecord(value) ? value : {};
  const flag = (key: string) => (typeof record[key] === 'boolean' ? (record[key] as boolean) : true);
  return {
    image_models: readModelRefs(record.image_models),
    writing_models: readModelRefs(record.writing_models),
    agent_cap_usd: asFiniteNumber(record.agent_cap_usd, 5),
    max_budget_usd: asFiniteNumber(record.max_budget_usd, 10),
    paid_x_search: flag('paid_x_search'),
    paid_photo_search: flag('paid_photo_search'),
    ai_labels: flag('ai_labels'),
    locked_image_model:
      readAgentModel(record.locked_image_model) ?? {
        provider: DEFAULT_GENERATION_SETTINGS.image.provider,
        model: DEFAULT_GENERATION_SETTINGS.image.model,
      },
    carousel_size: readImageSize(record.carousel_size) ?? { ...DEFAULT_GENERATION_SETTINGS.carousel_image },
  };
};

/** The agent tool settings in force. A failed read throws: the panel must not pass defaults off as the user's. */
export async function getAgentToolSettings(): Promise<AgentToolSettings> {
  const res = await axios.get<unknown>(`${BASE_URL}/settings/agent-tools`);
  return parseAgentToolSettings(res.data);
}

/**
 * Replaces the `agent_tools` section and nothing else (the generation section
 * has its own PUT, which would wipe it). A refusal (422 naming the value) reaches
 * the caller as the axios error.
 */
export async function putAgentToolSettings(values: AgentToolSettingsPayload): Promise<AgentToolSettings> {
  const res = await axios.put<unknown>(`${BASE_URL}/settings/agent-tools`, values);
  return parseAgentToolSettings(isRecord(res.data) ? res.data : values);
}

// ==================== API ====================

export const SettingsAPI = {
  /** The caller's full user-settings object ({} when nothing was ever saved). */
  async get(): Promise<Record<string, unknown>> {
    const res = await axios.get<Record<string, unknown>>(`${BASE_URL}/settings`);
    return res.data && typeof res.data === 'object' ? res.data : {};
  },

  /**
   * The caller's reviewer section alone, parsed. The backend has no
   * reviewer-only GET — the section lives in the one settings blob — so this is
   * the read + the parse in one place rather than in every caller.
   */
  async getReviewer(): Promise<ReviewerSettings> {
    return parseReviewerSettings(await SettingsAPI.get());
  },

  /** Replaces the validated `reviewer` section; other sections are untouched. */
  async putReviewer(values: ReviewerSettings): Promise<ReviewerSettings> {
    const res = await axios.put<ReviewerSettings>(`${BASE_URL}/settings/reviewer`, values);
    return res.data && typeof res.data === 'object' ? res.data : values;
  },

  /** The caller's media section. Unlike `reviewer`, it has a GET of its own. */
  async getMedia(): Promise<MediaSettings> {
    const res = await axios.get<Partial<MediaSettings>>(`${BASE_URL}/settings/media`);
    return parseMediaSettings(res.data);
  },

  /**
   * Replaces the `media` section; other sections are untouched. A bodiless
   * 200 keeps the values just sent rather than re-defaulting them — the write
   * succeeded, so "describing is on again" would be a lie about what is stored.
   */
  async putMedia(values: MediaSettings): Promise<MediaSettings> {
    const res = await axios.put<Partial<MediaSettings>>(`${BASE_URL}/settings/media`, values);
    return res.data && typeof res.data === 'object' ? parseMediaSettings(res.data) : values;
  },

  /**
   * The caller's effective generation settings: the stored section laid over the
   * hard defaults by the server, so "never saved" reads as the defaults.
   */
  async getGeneration(): Promise<GenerationSettings> {
    const res = await axios.get<unknown>(`${BASE_URL}/settings/generation`);
    return parseEffectiveGeneration(res.data);
  },

  /**
   * Replaces the validated `generation` section; other sections are untouched.
   * A refusal (400 with the reason, 503 when the registry is unreachable)
   * reaches the caller as the axios error. A bodiless 200 keeps the values just
   * sent (their modes unknown): the write succeeded, so showing the defaults
   * would misreport it.
   */
  async putGeneration(values: GenerationPayload): Promise<GenerationSettings> {
    const res = await axios.put<unknown>(`${BASE_URL}/settings/generation`, values);
    return parseEffectiveGeneration(looksLikeGenerationRecord(res.data) ? res.data : values);
  },
};

export default SettingsAPI;
